import _ from "lodash";
import ACLStore from "lib/aclStore";
import { goToRelativeRoute, openLinkInNewTab } from "modules/Services";
import { getRelativePath, dispatchError } from "Utils";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";
import {
  addViewCountForEntityMutation,
  createLibraryBookmarkMutation,
  removeLibraryBookmarkMutation,
} from "ToddleLibrary/modules/ToddleLibraryMutation";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  writeSaveByNodeToCache,
  getSaveByNodeFromCache,
  getLibraryCommonFeedFromCache,
  writeLibraryCommonFeedToCache,
  getLibraryCommonFeedV2FromCache,
  writeLibraryCommonFeedV2ToCache,
} from "ToddleLibrary/modules/ToddleLibraryGraphqlHelpers";
import {
  CATEGORISED_ENTITY_MAP,
  GLOBAL_FILTER,
  QUESTION,
  QUESTION_V2,
  WORKSHEET,
  MOCK_EXAM,
  WORKSHEET_ASSESSMENT,
  WORKSHEET_QUESTIONS_STORAGE_KEYS,
  WORKSHEET_FILTERS,
  QUESTION_FILTERS,
} from "./Constants";
import { getIsToddleLibraryWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";

const isAdditionalAllGradesFilterEnabled = ACLStore.can(
  "FeatureFlag:ToddleLibraryFiltersRevamp"
);

export const NAME = "toddleLibrary";

export const updateBookmarkState = ({
  title = "",
  entityId,
  entityType,
  followType = "SAVE",
  queryVariables = {},
  isWorksheet = false,
}) => {
  return async (dispatch, getState) => {
    const userInfo = getUserInfo({ portalType: "COMMUNITY" });
    let queryData = {};
    const isQuestionType = [QUESTION, QUESTION_V2].includes(entityType);
    const isMockExamType = _.isEqual(entityType, MOCK_EXAM);
    const isMYPWorksheet = _.isEqual(entityType, WORKSHEET_ASSESSMENT);
    const savedByData = getSaveByNodeFromCache({ entityId, entityType });
    const isSaved = _.get(savedByData, "isSavedForLibrary", false);

    const newBookmarkType =
      isQuestionType || isWorksheet || isMockExamType || isMYPWorksheet;

    const platformUserInfo = getUserInfo({ portalType: "PLANNER" });
    const platformUserId = _.get(platformUserInfo, "id", "");
    const state = getState();
    const userName = `${_.get(state, "login.userInfo.first_name", "")} ${_.get(
      state,
      "login.userInfo.last_name",
      ""
    )}`;

    let savedBy = _.get(savedByData, "savedBy", []);
    const userId = userInfo.id;
    const savedByMe = newBookmarkType
      ? isSaved
      : _.find(savedBy, item => _.get(item, "id") == userId);

    if (savedByMe) {
      savedBy = [];
    } else {
      savedBy = [
        {
          id: userId,
          __typename: "User",
        },
      ];
    }

    // When QUESTION is used with V2 enabled, the query uses QUESTION_V2 entity type
    const resolvedEntityType =
      _.isEqual(entityType, QUESTION) &&
      getIsToddleLibraryWorksheetQueriesV2Enabled()
        ? QUESTION_V2
        : entityType;
    const queryEntityType = isWorksheet
      ? WORKSHEET_ASSESSMENT
      : resolvedEntityType;

    if (
      savedByMe &&
      _.get(queryVariables, "feedType", "") == "SAVED" &&
      _.get(queryVariables, "entityTypes", "") == queryEntityType
    ) {
      queryData = getIsToddleLibraryWorksheetQueriesV2Enabled()
        ? getLibraryCommonFeedV2FromCache(queryVariables)
        : getLibraryCommonFeedFromCache(queryVariables);
    }

    const updateData = newBookmarkType
      ? update(savedByData, {
          isSavedForLibrary: { $set: !isSaved },
        })
      : update(savedByData, {
          savedBy: { $set: savedBy },
        });

    let updatedQueryData;
    if (!_.isEmpty(queryData)) {
      updatedQueryData = update(queryData, {
        node: {
          libraryEntities: {
            edges: {
              $set: _.filter(
                _.get(queryData, "node.libraryEntities.edges", []),
                item => _.get(item, "node.id", "") != entityId
              ),
            },
          },
        },
      });
      updatedQueryData = update(updatedQueryData, {
        node: {
          libraryEntities: {
            totalCount: {
              $set: _.get(queryData, "node.libraryEntities.totalCount", 0) - 1,
            },
            categorisedCount: {
              [CATEGORISED_ENTITY_MAP[queryEntityType]]: {
                $set:
                  _.get(
                    queryData,
                    `node.libraryEntities.categorisedCount.${CATEGORISED_ENTITY_MAP[queryEntityType]}`,
                    0
                  ) - 1,
              },
            },
          },
        },
      });
    }

    writeSaveByNodeToCache({ entityId, entityType, data: updateData });
    const writeFeedToCache = getIsToddleLibraryWorksheetQueriesV2Enabled()
      ? writeLibraryCommonFeedV2ToCache
      : writeLibraryCommonFeedToCache;
    try {
      if (!_.isEmpty(queryData)) {
        writeFeedToCache({
          variables: queryVariables,
          data: updatedQueryData,
        });
      }

      await client.mutate({
        mutation: savedByMe
          ? removeLibraryBookmarkMutation
          : createLibraryBookmarkMutation,
        variables: {
          entityId,
          entityType: queryEntityType,
          followType,
          portalType: "COMMUNITY",
        },
      });

      let toastMsg;
      const hasTitle = !_.isEmpty(title);
      if (hasTitle) {
        toastMsg = savedByMe
          ? "toastMsgs:removed_from_bookmarks_with_title"
          : "toastMsgs:added_to_bookmarks_with_title";
      } else {
        toastMsg = savedByMe
          ? "toastMsgs:removed_from_bookmarks"
          : "toastMsgs:added_to_bookmarks";
      }

      const toastMsgParams = {
        type: "success",
        msg: toastMsg,
      };

      if (hasTitle) {
        toastMsgParams.locale_params = [
          { key: "title", value: title, isPlainText: true },
        ];
      }

      dispatch(setToastMsg(toastMsgParams));

      return { isBookmarked: !savedByMe };
    } catch (e) {
      if (!_.isEmpty(queryData)) {
        writeFeedToCache({
          variables: queryVariables,
          data: queryData,
        });
      }

      writeSaveByNodeToCache({
        entityId,
        entityType,
        data: savedByData,
      });

      dispatchError({ dispatch, e });
      return {};
    }
  };
};

export const updateViewCountForToddleLibrary = ({ id, type, operation }) => {
  return async () => {
    try {
      await client.mutate({
        mutation: addViewCountForEntityMutation,
        variables: {
          input: {
            id,
            type,
            operation,
          },
        },
      });
    } catch (err) {
      console.error(err);
    }
  };
};

export const gotoLEDetailsView = ({
  id,
  mode = "view",
  isOpenInNewTab = false,
  replacePath = "",
  queryString = "",
}) => {
  return async dispatch => {
    const route = `assessment/${id}/${mode}${queryString}`;

    if (isOpenInNewTab) {
      const path = getRelativePath(route, `${replacePath}`);
      dispatch(openLinkInNewTab({ url: path }));
    } else {
      dispatch(goToRelativeRoute({ route, replacePath }));
    }
  };
};

export const goToUnitPreview = ({
  id,
  isOpenInNewTab = false,
  replacePath = "",
}) => {
  return async dispatch => {
    const isRevampEnabled = ACLStore.can(
      "FeatureFlag:NewUnitPlanViewInLibrary"
    );

    const route = isRevampEnabled ? `units/${id}` : `units/${id}/view`;

    if (isOpenInNewTab) {
      const path = getRelativePath(route, `${replacePath}`);
      dispatch(openLinkInNewTab({ url: path }));
    } else {
      dispatch(goToRelativeRoute({ route, replacePath }));
    }
  };
};

export const setupCurriculumProgramDependencies = ({
  curriculumProgramData,
  userOrganizationCurriculumPrograms,
}) => {
  return (dispatch, getState) => {
    const currentCurriculumProgram = _.find(curriculumProgramData, {
      type: CURRICULUM_TYPE_PYP,
    });

    const plannerElementSets = _.get(
      currentCurriculumProgram,
      "plannerElementSets"
    );

    const plannerElementSetsByType = _.reduce(
      plannerElementSets,
      (result, plannerElementSet) => {
        result[plannerElementSet.type] = plannerElementSet;
        return result;
      },
      {}
    );

    dispatch(updatePlannerElementSets(plannerElementSetsByType));
    dispatch(
      setUserOrganizationCurriculumPrograms(userOrganizationCurriculumPrograms)
    );
  };
};

export const resetQuestionAndWorksheetTopics = ({
  filterTypes,
  getFromStorage,
  saveIntoStorage,
}) => {
  return dispatch => {
    dispatch(
      updateFilter({
        filterType: filterTypes[WORKSHEET],
        params: WORKSHEET_FILTERS,
      })
    );
    dispatch(
      updateFilter({
        filterType: filterTypes[QUESTION],
        params: QUESTION_FILTERS,
      })
    );
    _.forEach(WORKSHEET_QUESTIONS_STORAGE_KEYS, key => {
      const savedFilters = JSON.parse(
        getFromStorage("toddleLibraryFilters") || "{}"
      );
      const filterValue = savedFilters[key];
      if (filterValue) {
        if (_.includes(key, "worksheet")) {
          const params = {
            ...filterValue,
            ...WORKSHEET_FILTERS,
          };
          savedFilters[key] = params;
        } else {
          const params = {
            ...filterValue,
            ...QUESTION_FILTERS,
          };
          savedFilters[key] = params;
        }
        saveIntoStorage("toddleLibraryFilters", JSON.stringify(savedFilters));
      }
    });
  };
};

export const applySavedGlobalFilters = ({ filterTypes, getFromStorage }) => {
  return dispatch => {
    const filterKey = "global-filter";
    const savedFilters = JSON.parse(
      getFromStorage("toddleLibraryFilters") || "{}"
    );
    if (savedFilters[filterKey]) {
      const { subject } = savedFilters[filterKey];
      const params = {
        subject,
      };
      dispatch(
        updateFilter({
          filterType: filterTypes[GLOBAL_FILTER],
          params,
        })
      );
    }
  };
};

export const goToLECollection = ({ id }) => {
  return async dispatch => {
    const route = `collection/${id}`;
    dispatch(goToRelativeRoute({ route }));
  };
};

export const getUnitDetailsRoute = (unitPlanId, mode, routePostFix) => {
  return `/toddle-library/units/${unitPlanId}/${mode}${routePostFix}`;
};

//redux
export const UPDATE_PARENT_ENTITY = "UPDATE_PARENT_ENTITY " + NAME;
export const UPDATE_FILTER = "UPDATE_FILTER " + NAME;
export const UPDATE_PLANNER_ELEMENT_SETS =
  "UPDATE_PLANNER_ELEMENT_SETS" + " " + NAME;
export const UPDATE_SHOW_PREMIUM_BANNER = "UPDATE_SHOW_PREMIUM_BANNER " + NAME;
export const UPDATE_SHOW_WORKSHEET_PREMIUM_BANNER =
  "UPDATE_SHOW_WORKSHEET_PREMIUM_BANNER " + NAME;
export const UPDATE_SHOW_QUESTION_PREMIUM_BANNER =
  "UPDATE_SHOW_QUESTION_PREMIUM_BANNER " + NAME;
export const SET_USER_ORAGNIZATION_CURRICULUM_PROGRAMS =
  "SET_USER_ORAGNIZATION_CURRICULUM_PROGRAMS " + NAME;
export const UPDATE_RECENT_WORKSHEET_ID = "UPDATE_RECENT_WORKSHEET_ID " + NAME;
export const UPDATE_SHOULD_DO_DEFAULT_LANDING =
  "UPDATE_SHOULD_DO_DEFAULT_LAND " + NAME;
export const UPDATE_SEARCH_PAGE_SOURCE = "UPDATE_SEARCH_PAGE_SOURCE " + NAME;
export const UPDATE_TOPICS_MAP = "UPDATE_TOPICS_MAP " + NAME;
export const UPDATE_SUBJECT_LEVELS = "UPDATE_SUBJECT_LEVELS" + NAME;
export const UPDATE_IS_LEARNING_COURSE_ENABLED_FOR_ORG =
  "UPDATE_IS_LEARNING_COURSE_ENABLED_FOR_ORG" + NAME;
export const UPDATE_INITIAL_FEED_SEARCH_TEXT =
  "UPDATE_INITIAL_FEED_SEARCH_TEXT  " + NAME;
export const UPDATE_MODULE_EMPTY_STATUS = "UPDATE_MODULE_EMPTY_STATUS " + NAME;

export const updateIsLearningCourseEnabledForOrgInToddleLibrary = payload => {
  return {
    type: UPDATE_IS_LEARNING_COURSE_ENABLED_FOR_ORG,
    payload,
  };
};

export const updateModuleEmptyStatus = payload => ({
  type: UPDATE_MODULE_EMPTY_STATUS,
  payload,
});

export const updateParentEntity = payload => ({
  type: UPDATE_PARENT_ENTITY,
  payload,
});

export const updateFilter = payload => ({
  type: UPDATE_FILTER,
  payload,
});

export const updatePlannerElementSets = payload => ({
  type: UPDATE_PLANNER_ELEMENT_SETS,
  payload,
});

export const updateShowPremiumBanner = payload => ({
  type: UPDATE_SHOW_PREMIUM_BANNER,
  payload,
});

export const updateShowWorksheetPremiumBanner = payload => ({
  type: UPDATE_SHOW_WORKSHEET_PREMIUM_BANNER,
  payload,
});

export const updateShowQuestionPremiumBanner = payload => ({
  type: UPDATE_SHOW_QUESTION_PREMIUM_BANNER,
  payload,
});

export const setUserOrganizationCurriculumPrograms = payload => ({
  type: SET_USER_ORAGNIZATION_CURRICULUM_PROGRAMS,
  payload,
});

export const updateRecentWorksheetId = payload => ({
  type: UPDATE_RECENT_WORKSHEET_ID,
  payload,
});

export const updateShouldDoDefaultLanding = payload => ({
  type: UPDATE_SHOULD_DO_DEFAULT_LANDING,
  payload,
});

export const updateSearchPageSource = payload => ({
  type: UPDATE_SEARCH_PAGE_SOURCE,
  payload,
});

export const updateTopicsMap = payload => ({
  type: UPDATE_TOPICS_MAP,
  payload,
});

export const updateSubjectLevels = payload => ({
  type: UPDATE_SUBJECT_LEVELS,
  payload,
});

export const updateInitialFeedSearchText = payload => ({
  type: UPDATE_INITIAL_FEED_SEARCH_TEXT,
  payload,
});

const REDUCER_HANDLERS = {
  [UPDATE_PARENT_ENTITY]: (state, action) => {
    const params = action.payload;
    return update(state, {
      parentEntity: { $set: params },
    });
  },
  [UPDATE_FILTER]: (state, action) => {
    const { params, filterType } = action.payload;
    if (_.isEmpty(filterType)) return state;
    _.forEach(params, (value, key) => {
      state = update(state, {
        [filterType]: {
          [key]: { $set: value },
        },
      });
    });
    return state;
  },
  [UPDATE_PLANNER_ELEMENT_SETS]: (state, action) => {
    const params = action.payload;
    return update(state, {
      currentPlannerElementSets: { $set: params },
    });
  },
  [UPDATE_SHOW_PREMIUM_BANNER]: (state, action) => {
    const params = action.payload;
    return update(state, {
      showPremiumBanner: { $set: params },
    });
  },
  [UPDATE_SHOW_WORKSHEET_PREMIUM_BANNER]: (state, action) => {
    const params = action.payload;
    return update(state, {
      showWorksheetPremiumBanner: { $set: params },
    });
  },
  [UPDATE_SHOW_QUESTION_PREMIUM_BANNER]: (state, action) => {
    const params = action.payload;
    return update(state, {
      showQuestionPremiumBanner: { $set: params },
    });
  },
  [SET_USER_ORAGNIZATION_CURRICULUM_PROGRAMS]: (state, action) => {
    const params = action.payload;
    return update(state, {
      userOrganizationCurriculumPrograms: { $set: params },
    });
  },
  [UPDATE_RECENT_WORKSHEET_ID]: (state, action) => {
    const params = action.payload;
    return update(state, {
      recentWorksheetId: { $set: params },
    });
  },
  [UPDATE_SHOULD_DO_DEFAULT_LANDING]: (state, action) => {
    const params = action.payload;
    return update(state, {
      shouldDoDefaultLanding: { $set: params },
    });
  },
  [UPDATE_SEARCH_PAGE_SOURCE]: (state, action) => {
    const params = action.payload;
    return update(state, {
      searchPageSource: { $set: params },
    });
  },
  [UPDATE_TOPICS_MAP]: (state, action) => {
    const params = action.payload;
    return update(state, {
      topicsMap: { $set: params },
    });
  },
  [UPDATE_SUBJECT_LEVELS]: (state, action) => {
    const params = action.payload;
    return update(state, {
      subjectLevels: { $set: params },
    });
  },
  [UPDATE_IS_LEARNING_COURSE_ENABLED_FOR_ORG]: (state, action) => {
    const params = action.payload;
    return update(state, {
      isLearningCourseEnabledForOrg: { $set: params },
    });
  },
  [UPDATE_INITIAL_FEED_SEARCH_TEXT]: (state, action) => {
    const params = action.payload;
    return update(state, {
      feedFilter: {
        initialSearchText: { $set: params },
      },
    });
  },
  [UPDATE_MODULE_EMPTY_STATUS]: (state, action) => {
    const { currentModuleId, isCurrentModuleEmpty } = action.payload;
    return update(state, {
      currentModuleId: { $set: currentModuleId },
      isCurrentModuleEmpty: { $set: isCurrentModuleEmpty },
    });
  },
};

const initialState = {
  parentEntity: "",
  globalFilter: {
    grade: {},
    subject: {},
  },
  leFilter: {
    sortBy: {},
  },
  unitFilter: {
    ibThemes: [],
    unitType: {},
    sortBy: {},
  },
  searchFilter: {
    selectedEntity: "",
    ibThemes: [],
    unitType: {},
    sortBy: {},
    topic: {},
    contentType: { key: "all", label: "All", value: "ALL" },
    hidePrevious: false,
  },
  feedFilter: {
    selectedEntity: "",
    initialSearchText: "",
    ibThemes: [],
    unitType: {},
    sortBy: {},
    topic: {},
    contentType: { key: "all", label: "All", value: "ALL" },
    hidePrevious: isAdditionalAllGradesFilterEnabled ? false : undefined,
  },
  bookmarkFilter: {
    selectedEntity: "",
    grade: {},
  },
  questionFilter: {
    topics: [],
    subjectLevels: [],
    subTopics: [],
    paperTypes: [],
    questionTypes: [],
    difficulty: [],
    hidePrevious: false,
    relatedConcepts: [],
    keyConcepts: [],
    globalContext: [],
    criterion: [],
    contentType: isAdditionalAllGradesFilterEnabled
      ? { key: "all", label: "All", value: "ALL" }
      : undefined,
  },
  searchPageSource: "",
  worksheetFilter: {
    types: [],
    topics: [],
    subjectLevels: [],
    paperTypes: [],
    relatedConcepts: [],
    keyConcepts: [],
    globalContext: [],
    criterion: [],
    subTopics: [],
    ...(isAdditionalAllGradesFilterEnabled && {
      contentType: { key: "all", label: "All", value: "ALL" },
      hidePrevious: false,
    }),
  },
  topicFilter: {
    selectedEntity: "",
  },
  currentPlannerElementSets: {},
  showPremiumBanner: false,
  userOrganizationCurriculumPrograms: [],
  showWorksheetPremiumBanner: false,
  showQuestionPremiumBanner: false,
  recentWorksheetId: "",
  shouldDoDefaultLanding: false,
  topicsMap: [],
  subjectLevels: [],
  isLearningCourseEnabledForOrg: false,
  currentModuleId: "",
  isCurrentModuleEmpty: false,
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
